import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Input, Button, Spin, message, Select } from "antd";
import { useDispatch } from "react-redux";
import { login } from "../../store";
import { baseUrl } from "../../constants/api";
import { API_ENDPOINTS } from "../../constants/endpoints";
import LogoImg from "../../assets/images/Railway.png";
import config from "../../config.json";
import AuthCarousel from "../../components/AuthCarousel/AuthCarousel";
import designationData from "../../constants/designations.json";

const { Option } = Select;

interface CliOption {
  _id: string;
  name: string;
  reference_id: string;
}

const getShortSignupError = (error: string, fallback: string) => {
  const normalizedError = error.toLowerCase();
  if (normalizedError.includes("already exists")) {
    return "Account already exists. Please sign in.";
  }
  if (normalizedError.includes("invalid reference")) {
    return "HRMS ID not found.";
  }
  if (normalizedError.includes("selected cli") || normalizedError.includes("please select a cli")) {
    return "Please select a valid CLI.";
  }
  return fallback;
};

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [hrmsId, setHrmsId] = useState("");
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [signupCliId, setSignupCliId] = useState("");
  const [signupCliOptions, setSignupCliOptions] = useState<CliOption[]>([]);
  const [signupCliLoading, setSignupCliLoading] = useState(false);
  const [signupCliError, setSignupCliError] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isSignUp, setIsSignUp] = useState(location.pathname === "/sign-up");

  useEffect(() => {
    const signingUp = location.pathname === "/sign-up";
    setIsSignUp(signingUp);
    setPhone("");
    setHrmsId("");
    setName("");
    setDesignation("");
    setSignupCliId("");
    setSignupCliOptions([]);
    setSignupCliError("");
  }, [location.pathname]);

  const isCliDesignation = designation.trim().toUpperCase() === "CHIEF LOCO INSPECTOR";

  useEffect(() => {
    setSignupCliOptions([]);
    setSignupCliId("");
    setSignupCliError("");

    if (!isSignUp || isCliDesignation || !hrmsId.trim()) {
      setSignupCliLoading(false);
      return;
    }

    setSignupCliLoading(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        const query = new URLSearchParams({ reference_id: hrmsId.trim() });
        const response = await fetch(
          `${baseUrl}${API_ENDPOINTS.GET_SIGNUP_CLI_OPTIONS}?${query.toString()}`,
          { signal: controller.signal }
        );
        const responseText = await response.text();
        let result: { data?: CliOption[]; message?: string } = {};
        if (responseText) {
          try {
            result = JSON.parse(responseText);
          } catch {
            throw new Error("Unable to load CLI list. Please retry.");
          }
        }
        if (!response.ok) {
          throw new Error(getShortSignupError(result.message || "", "Unable to load CLI list. Please retry."));
        }
        setSignupCliOptions(result.data || []);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setSignupCliError(error instanceof Error
          ? getShortSignupError(error.message, "Unable to load CLI list. Please retry.")
          : "Unable to load CLI list. Please retry.");
      } finally {
        if (!controller.signal.aborted) {
          setSignupCliLoading(false);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [hrmsId, isCliDesignation, isSignUp]);

  const handleAuth = async () => {
    if (isSignUp && !isCliDesignation && !signupCliId) {
      message.error("Please select a CLI.");
      return;
    }

    setLoading(true);

    if (isSignUp) {
      const apiUrl = `${baseUrl}/api/users/create-user`;
      const userData = {
        name,
        phone,
        reference_id: hrmsId,
        designation,
        ...(!isCliDesignation && { cli_id: signupCliId }),
      };

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        });

        if (response.ok) {
          await response.json();
          message.success("User created successfully! Redirecting to login...");
          // Switch to login tab and clear signup specific fields or just navigate
          setTimeout(() => {
            navigate("/sign-in");
            setName("");
            setDesignation("");
            setPhone("");
            setHrmsId("");
            setSignupCliId("");
          }, 2000);
        } else {
          const result = await response.json().catch(() => null);
          message.error(getShortSignupError(result?.message || "", "Unable to create account. Please try again."));
        }
      } catch (error) {
        message.error("An error occurred while creating the user. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      const apiUrl = `${baseUrl}/api/users/login`;
      const loginData = {
        phone,
        reference_id: hrmsId,
      };

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(loginData),
        });

        if (response.ok) {
          const result = await response.json();
          const normalizedRole = result.role?.toUpperCase?.() || "";

          if (normalizedRole === "ADMIN") {
            dispatch(
              login({
                loggedIn: true,
                token: result.token,
                profile: {
                  ...(result.profile || {}),
                  role: "ADMIN",
                },
              })
            );
            message.success("Admin Login Successful !");
            navigate("/");
          } else {
            dispatch(
              login({
                loggedIn: true,
                token: result.token,
                profile: {
                  ...(result.profile || {}),
                  role: result.role,
                },
                user_id: result.user_id,
              })
            );

            message.success("Login successful !");
            navigate(normalizedRole === "CLI" ? "/participants" : "/");
          }
        } else {
          const errorText = await response.text();
          console.log("Login failed", errorText);
          message.error("Wrong Credentials!");
        }
      } catch (error) {
        message.error("An error occurred while logging in.");
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleMode = () => {
    if (isSignUp) {
      navigate("/sign-in");
    } else {
      navigate("/sign-up");
    }
  };

  return (
    <div className="auth-page-container">
      <div className="auth-left-section">
        <div className="login-card-logo">
          <img className="logoImg" src={LogoImg} alt="Logo" />
          <div className="login-card-logo-text">
            <div className="login-card-logo-text-1" style={{ color: config.primary_color }}>{config.app_name}</div>
            <div className="login-card-logo-text-2">The East Coast Railway Quiz</div>
          </div>
        </div>
        <div className="login-card">
          <div className="login-card-inner-view">
            <h2 className="login-title">{isSignUp ? "Create Account" : "Enter Details"}</h2>
            <Form
              layout="vertical"
              onFinish={handleAuth}
              className="login-form"
              autoComplete="off"
              key={isSignUp ? "signup-form" : "login-form"}
            >
              {isSignUp && (
                <Form.Item
                  rules={[{ required: true, message: "Please enter your name!" }]}
                  style={{ width: "100%", marginBottom: "16px" }}
                >
                  <Input
                    placeholder="Name"
                    className="custom-input"
                    name="signup-name"
                    autoComplete="off"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Form.Item>
              )}

              <Form.Item
                rules={[
                  {
                    required: true,
                    message: "Please enter your phone number!",
                  },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: "Phone number must be 10 digits!",
                  },
                ]}
                style={{ width: "100%", marginBottom: "16px" }}
              >
                <Input
                  placeholder="Enter your phone no."
                  className="custom-input"
                  name={isSignUp ? "signup-phone" : "login-phone"}
                  autoComplete="off"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </Form.Item>

              <Form.Item
                rules={[{ required: true, message: "Please enter your HRMS ID!" }]}
                style={{ width: "100%", marginBottom: "16px" }}
              >
                <Input
                  placeholder="Enter your HRMS ID"
                  className="custom-input"
                  name={isSignUp ? "signup-reference-id" : "login-reference-id"}
                  autoComplete="off"
                  value={hrmsId}
                  onChange={(e) => setHrmsId(e.target.value)}
                  onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    const target = e.target as HTMLInputElement;
                    setHrmsId(target.value.toUpperCase());
                  }}
                  required
                />
              </Form.Item>

              {isSignUp && !isCliDesignation && (
                <>
                  <Form.Item
                    name="cli_id"
                    rules={[{ required: true, message: "Select a CLI." }]}
                    style={{ width: "100%", marginBottom: "16px" }}
                  >
                    <Select
                      placeholder={signupCliLoading ? "Loading CLI list..." : "Select your CLI"}
                      className="custom-select-designation"
                      value={signupCliId || undefined}
                      loading={signupCliLoading}
                      disabled={signupCliLoading || signupCliOptions.length === 0}
                      onChange={(value) => setSignupCliId(value)}
                    >
                      {signupCliOptions.map((cli) => (
                        <Option key={cli._id} value={cli._id}>
                          {cli.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {signupCliError && (
                    <div role="alert" style={{ color: "#ff4d4f", marginTop: "-4px", marginBottom: "12px" }}>
                      {signupCliError}
                    </div>
                  )}
                  {!signupCliLoading && !signupCliError && hrmsId.trim() && signupCliOptions.length === 0 && (
                    <div style={{ color: "#8c8c8c", marginTop: "-4px", marginBottom: "12px" }}>
                      No CLIs found for this HRMS ID.
                    </div>
                  )}
                </>
              )}

              {isSignUp && (
                <Form.Item
                  name="designation"
                  rules={[{ required: true, message: "Please select your designation!" }]}
                  style={{ width: "100%", marginBottom: "16px" }}
                >
                  <Select
                    placeholder="Select Designation"
                    className="custom-select-designation"
                    value={designation || undefined}
                    onChange={(value) => setDesignation(value)}
                  >
                    {designationData.designations.map((d) => (
                      <Option key={d} value={d}>
                        {d}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {loading ? (
                <Spin size="large" />
              ) : (
                <Button
                  type="primary"
                  htmlType="submit"
                  className="login-button"
                  disabled={loading}
                >
                  {isSignUp ? "Sign up" : "Login"}
                </Button>
              )}
            </Form>
            <div className="signup-text">
              {isSignUp ? "Already have an account? " : "Don't have an account? "}
              <span className="signup-link" onClick={toggleMode}>
                {isSignUp ? "Sign In" : "Sign Up"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-right-section">
        <AuthCarousel />
      </div>
    </div>
  );
};

export default LoginPage;
